import { cloneElement, isValidElement, useContext, useEffect } from "react";
import { Form as AntForm } from "antd";
import { createStyles } from "antd-style";

import { useFormListParentName } from "../form-list/FormListContext";
import { FormMetaContext, useEntityPermissions } from "../meta-context/FormMetaContext";
import {
  assertValidPropsInDev,
  resolveLabelNameString,
  useFieldDataMapper,
} from "../utils/useFieldDataMapper";

import type { CustomFormItemProps } from "../types";

const useStyles = createStyles(({ token, css }, props: { isLabelEmpty?: boolean }) => ({
  itemStyles: css`
    margin-bottom: ${token.marginSM}px;
    &.ant-form-item .ant-form-item-label {
      display: ${props.isLabelEmpty ? "none" : "block"};
    }
    &.ant-form-item .ant-form-item-label > label:not(.ant-form-item-required) {
      align-items: flex-start;
      height: auto;
      overflow: visible;
    }
  `,
}));

function CustomFormItem(props: CustomFormItemProps) {
  const {
    labelString,
    name,
    label,
    testIdPrefix,
    children,
    permissionTargetField,
    ...formItemProps
  } = props;
  const { trackProps } = useContext(FormMetaContext);
  const permissions = useEntityPermissions();
  const { splitName } = useFieldDataMapper();
  const { styles } = useStyles({ isLabelEmpty: !label });
  const formListParentName = useFormListParentName();
  const effectivePermissionTargetField = permissionTargetField || name;

  assertValidPropsInDev(props);
  const resolvedLabel = resolveLabelNameString(name, label, labelString);

  useEffect(() => {
    if (name && resolvedLabel) {
      trackProps(name, {
        label: resolvedLabel,
        formListParentName,
        testIdPrefix,
      });
    }
  }, [trackProps, resolvedLabel, name, formListParentName, testIdPrefix]);

  if (
    effectivePermissionTargetField &&
    !permissions.isFieldViewable(effectivePermissionTargetField)
  ) {
    return null;
  }

  let clonedChildren = children;

  let isChildDisabled = false;

  if (isValidElement<{ disabled?: boolean }>(children) && children.props.disabled === true) {
    isChildDisabled = true;
  } else if (effectivePermissionTargetField) {
    isChildDisabled = !permissions.isFieldEditable(effectivePermissionTargetField);
  }

  if (isValidElement(clonedChildren)) {
    clonedChildren = cloneElement(clonedChildren, {
      ["aria-label"]: resolvedLabel,
      disabled: isChildDisabled,
    } as React.HTMLAttributes<HTMLElement>);
  }

  return (
    <AntForm.Item
      name={splitName(name)}
      label={label && <span data-testid={testIdPrefix + "-form-label"}>{label}</span>}
      className={styles.itemStyles}
      data-testid={testIdPrefix + "-form-item"}
      {...formItemProps}
    >
      {clonedChildren}
    </AntForm.Item>
  );
}

export { CustomFormItem as FormItem };
